import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FiMail, FiLock, FiChevronRight, FiArrowRight, FiGlobe } from 'react-icons/fi';
import { BsStars } from 'react-icons/bs';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/auth.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'login', email, password }),
            });

            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (pErr) {
                console.error('Raw response from server:', responseText);
                throw new Error('Server returned invalid data format. Check console for details.');
            }

            if (data.success) {
                localStorage.setItem('user', JSON.stringify(data.data.user));
                const role = data.data.user.role;
                if (role === 'super_admin') navigate('/super-admin');
                else if (role === 'district_admin') navigate('/district-admin');
                else if (role === 'club_admin') navigate('/club-admin');
                else if (role === 'member') navigate('/member-dashboard');
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Failed to connect to server. Please check your internet connection or try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-[#FAFAFA] font-sans">
            {/* Left Column - Dark Theme Presentation */}
            <div className="hidden lg:flex lg:w-[55%] bg-[#0B0F19] text-white p-12 xl:p-20 flex-col justify-between relative overflow-hidden">
                {/* Background ambient lighting */}
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
                
                {/* Top Brand Name */}
                <div className="flex items-center gap-3 text-sm font-semibold tracking-[0.2em] text-gray-400 z-10 w-full relative">
                    <FiGlobe className="text-[#D4AF37] text-lg" />
                    LEO ATTENDANCE PORTAL
                </div>

                {/* Center Content */}
                <div className="flex-1 flex flex-col justify-center z-10 mt-12 w-full max-w-2xl relative">
                    {/* Big Logo Design */}
                    <div className="mb-16 flex items-center">
                        <img 
                            src="/club.png" 
                            alt="Leo Club Logo" 
                            className="h-28 xl:h-36 w-auto object-contain drop-shadow-2xl brightness-110 transform transition-transform duration-700 hover:scale-105" 
                        />
                    </div>

                    <div className="mb-8 pl-1">
                        <h1 className="text-4xl xl:text-5xl font-light mb-4 text-gray-200">
                            The Future of
                        </h1>
                        <h1 className="text-5xl xl:text-6xl font-extrabold text-white tracking-tight flex items-end gap-2">
                            Club Leadership<span className="text-[#D4AF37]">.</span>
                        </h1>
                    </div>
                    
                    <p className="text-gray-400 text-sm xl:text-lg leading-relaxed max-w-md font-light mb-16 pl-1">
                        Seamless attendance tracking and district reporting powered by next-generation cloud architecture.
                    </p>

                    {/* Registration Card */}
                    <div className="bg-[#13192B] border border-gray-800/60 rounded-3xl p-8 max-w-[500px] relative group hover:border-gray-700/60 transition-colors duration-500 overflow-hidden">
                        <div className="absolute top-6 right-6 text-gray-600 group-hover:text-blue-500 transition-colors duration-500">
                            <BsStars className="text-2xl" />
                        </div>
                        <div className="text-[10px] font-bold tracking-[0.2em] text-[#D4AF37] mb-3">NEW ORGANIZATION</div>
                        <h3 className="text-xl xl:text-2xl font-bold text-white mb-2">Want to add your club?</h3>
                        <p className="text-xs xl:text-sm text-gray-400 mb-6 font-light">
                            Initialize your club's digital workspace in seconds.
                        </p>
                        <button
                            onClick={() => navigate('/register-club')}
                            className="flex items-center gap-3 text-[11px] font-bold tracking-[0.15em] text-white hover:text-[#D4AF37] transition-colors"
                        >
                            REGISTER NOW <FiArrowRight className="text-sm" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Right Column - Login Form */}
            <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-12 relative bg-white lg:bg-[#FCFCFC] lg:shadow-[inset_20px_0_40px_rgba(0,0,0,0.02)] min-h-screen">
                {/* Mobile Top Brand (only visible on small screens) */}
                <div className="absolute top-8 left-8 flex lg:hidden items-center gap-2 text-xs font-bold tracking-widest text-gray-800">
                    <FiGlobe className="text-[#D4AF37]" /> LEO PORTAL
                </div>

                <div className="w-full max-w-[440px] bg-white rounded-3xl p-8 sm:p-12 lg:shadow-[0_8px_40px_rgba(0,0,0,0.04)] lg:border border-gray-100 mt-12 lg:mt-0">
                    <div className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-3">Sign In</h2>
                        <p className="text-sm text-gray-500 font-light">Enter your credentials to access the dashboard.</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-8">
                        {error && (
                            <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-100 flex items-start gap-2">
                                <span className="font-semibold text-red-700 mt-0.5">!</span>
                                <p>{error}</p>
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Email Input */}
                            <div className="space-y-4">
                                <label className="block text-[10px] font-bold tracking-[0.15em] text-gray-400 uppercase">
                                    Email Address
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none text-gray-300 group-focus-within:text-blue-500 transition-colors">
                                        <FiMail className="text-xl" />
                                    </div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full pl-10 pr-4 py-3 bg-transparent border-b border-gray-200 focus:border-black transition-colors outline-none text-gray-800 font-medium text-[15px] placeholder-gray-300"
                                        placeholder="admin@district306.org"
                                    />
                                </div>
                            </div>

                            {/* Password Input */}
                            <div className="space-y-4 pt-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-[10px] font-bold tracking-[0.15em] text-gray-400 uppercase">
                                        Password
                                    </label>
                                    <a href="#" className="flex-shrink-0 text-[10px] font-bold tracking-[0.15em] text-[#D4AF37] hover:text-[#b8952b] transition-colors uppercase pt-1">
                                        Forgot?
                                    </a>
                                </div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none text-gray-300 group-focus-within:text-blue-500 transition-colors">
                                        <FiLock className="text-xl" />
                                    </div>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="w-full pl-10 pr-4 py-3 bg-transparent border-b border-gray-200 focus:border-black transition-colors outline-none text-gray-800 font-medium text-[15px] placeholder-gray-300 tracking-widest"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-8">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#000000] hover:bg-[#111111] text-white font-semibold py-4 rounded-xl flex items-center justify-between px-6 transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none group"
                            >
                                <span className="text-sm tracking-wide pl-2">{loading ? 'Authenticating...' : 'Secure Login'}</span>
                                {!loading && <FiChevronRight className="text-xl text-gray-400 group-hover:text-white transition-transform duration-300 group-hover:translate-x-1" />}
                            </button>
                        </div>
                        
                        {/* Mobile Registration Link */}
                        <div className="mt-8 text-center sm:hidden">
                            <p className="text-xs text-gray-500">
                                Want to add your club?{' '}
                                <button
                                    type="button"
                                    onClick={() => navigate('/register-club')}
                                    className="text-black font-bold ml-1 border-b border-black hover:text-blue-600 hover:border-blue-600 transition-colors"
                                >
                                    Register Now
                                </button>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;

